/**
 * Which screen each task is performed in.
 *
 * The task *registry* is the server's — `GET /api/tasks` decides what tasks exist, what they
 * are scoped to and when an image is done with one. Duplicating that here would be a second
 * source of truth free to drift from the done-predicates the numbers are computed with.
 *
 * What the client owns is the surface: boxing happens on the Konva canvas, setting a direction
 * happens on a page with an image and two selects. That mapping cannot live on the server —
 * it is a fact about this build — so it lives here, and it is the one place a new task's view
 * is wired up.
 *
 * A task the server offers and this table does not know is **not** hidden. It is shown with
 * its numbers and without a Start button, saying so: silently dropping it would make a
 * deployed frontend older than its API look like an API with less work in it. The reverse —
 * an entry here for a task the server does not offer — simply never renders.
 *
 * Deliberately Svelte-free, so it can be unit-tested in a plain node environment. The `Route`
 * import is type-only and erases at build time.
 */

import type { TaskQueueItem } from './api/types';
import type { Route } from './router.svelte';

export interface TaskView {
  /** The task id, as the server states it. */
  id: string;
  /** Where one unit of this task's work opens. */
  open: (imageId: number) => Route;
  /** What the annotator does there, for the row button. One or two words. */
  action: string;
  /**
   * Where complete-and-advance goes next. `open-work` (the default) looks for an image still
   * open for boxing — a sibling photograph first, then the next record's. `queue-item` takes the
   * next record in the queue and opens the image the server chose for it: right for a task whose
   * work is on photographs the boxing has already finished, where no image is "open" and the
   * first rule would always fall back to the queue page.
   */
  advance?: 'open-work' | 'queue-item';
}

export const TASK_VIEWS: readonly TaskView[] = [
  {
    id: 'letter-boxing',
    open: (imageId) => ({ name: 'image', imageId }),
    action: 'Box',
  },
  {
    id: 'reading-direction',
    open: (imageId) => ({ name: 'direction', imageId }),
    action: 'Set direction',
  },
  {
    // The box editor again: settling a chip is a box or `N`, and making two copies agree is
    // the same two gestures on the copy that is short. Nothing here needs a screen of its own.
    id: 'second-pass',
    open: (imageId) => ({ name: 'image', imageId }),
    action: 'Review',
    advance: 'queue-item',
  },
];

export function taskView(id: string | null | undefined): TaskView | undefined {
  if (!id) return undefined;
  return TASK_VIEWS.find((view) => view.id === id);
}

/** The route a queue row opens, or null when this build cannot perform the task. */
export function taskItemRoute(taskId: string | null, imageId: number | null): Route | null {
  const view = taskView(taskId);
  if (!view || imageId === null) return null;
  return view.open(imageId);
}

/** True when complete-and-advance should take the queue's next item rather than hunt for open work. */
export function advancesByQueueItem(taskId: string | null | undefined): boolean {
  return taskView(taskId)?.advance === 'queue-item';
}

/**
 * A queue row's reasons, defensively.
 *
 * `reasons` arrived with the second pass. A bundle deployed ahead of its API gets rows without
 * the field, and reading `.length` off `undefined` would take down all three queues at once —
 * so every read goes through here.
 */
export function itemReasons(item: Pick<TaskQueueItem, 'reasons'> | null | undefined): string[] {
  return item?.reasons ?? [];
}
